import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { RegistrationResponse } from '@/modules/onboarding/registration/store/registrationStore';
import { useCallback, useEffect, useState } from 'react';
import { uploadDocument } from '../services/documentUploadService';
import { type DocumentType } from '../utils/documentTypeMapping';

export interface UploadStatus {
    status: 'idle' | 'uploading' | 'uploaded' | 'error';
    contentDocumentId?: string;
    error?: string;
}

export interface UseDocumentUploadResult {
    uploadStatus: Record<DocumentType, UploadStatus>;
    uploadDocument: (documentType: DocumentType, fileUri: string) => Promise<void>;
    retryUpload: (documentType: DocumentType, fileUri: string) => Promise<void>;
    resetUploadStatus: (documentType: DocumentType) => void;
    clearUploadStatus: (documentType: DocumentType) => void;
    isUploading: boolean;
    hasUploading: boolean;
    isInitialized: boolean;
}

const INITIAL_UPLOAD_STATUS: Record<DocumentType, UploadStatus> = {
    emiratesIdFront: { status: 'idle' },
    emiratesIdBack: { status: 'idle' },
    passport: { status: 'idle' },
};

const UPLOAD_STATUS_STORAGE_KEY = 'document.upload.status';

// Load upload status from storage
async function loadUploadStatusFromStorage(): Promise<Record<DocumentType, UploadStatus>> {
    try {
        const stored = await asyncStorage.getItem(UPLOAD_STATUS_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored) as Record<DocumentType, UploadStatus>;
            logger.info('useDocumentUpload', 'Loaded upload status from storage', {
                statuses: Object.keys(parsed),
            });
            return parsed;
        }
    } catch (error) {
        logger.warn('useDocumentUpload', 'Failed to load upload status from storage', error);
    }
    return INITIAL_UPLOAD_STATUS;
}

// Save upload status to storage
async function saveUploadStatusToStorage(status: Record<DocumentType, UploadStatus>): Promise<void> {
    try {
        await asyncStorage.setItem(UPLOAD_STATUS_STORAGE_KEY, JSON.stringify(status));
        logger.debug('useDocumentUpload', 'Saved upload status to storage');
    } catch (error) {
        logger.error('useDocumentUpload', 'Failed to save upload status to storage', error);
    }
}

export function useDocumentUpload(
    registrationResponse: RegistrationResponse | null
): UseDocumentUploadResult {
    const [uploadStatus, setUploadStatus] = useState<Record<DocumentType, UploadStatus>>(
        INITIAL_UPLOAD_STATUS
    );
    const [isInitialized, setIsInitialized] = useState(false);

    // Load upload status from storage on mount
    useEffect(() => {
        let isMounted = true;
        loadUploadStatusFromStorage().then((loadedStatus) => {
            if (isMounted) {
                setUploadStatus(loadedStatus);
                setIsInitialized(true);
                logger.info('useDocumentUpload', 'Initialized upload status from storage', {
                    statuses: Object.entries(loadedStatus).map(([key, value]) => ({
                        documentType: key,
                        status: value.status,
                    })),
                });
            }
        });
        return () => {
            isMounted = false;
        };
    }, []);

    // Persist upload status to storage whenever it changes
    useEffect(() => {
        if (isInitialized) {
            void saveUploadStatusToStorage(uploadStatus);
        }
    }, [uploadStatus, isInitialized]);

    const handleUpload = useCallback(
        async (documentType: DocumentType, fileUri: string) => {
            logger.info('useDocumentUpload', 'handleUpload called', {
                documentType,
                hasRegistrationResponse: !!registrationResponse,
                fileUri,
            });

            if (!registrationResponse) {
                logger.warn('useDocumentUpload', 'No registration response available', {
                    documentType,
                });
                setUploadStatus((prev) => ({
                    ...prev,
                    [documentType]: {
                        status: 'error',
                        error: 'Registration data not available',
                    },
                }));
                return;
            }

            logger.info('useDocumentUpload', 'Setting upload status to uploading', { documentType });

            // Set uploading status
            setUploadStatus((prev) => ({
                ...prev,
                [documentType]: { status: 'uploading' },
            }));

            try {
                const result = await uploadDocument({
                    documentType,
                    fileUri,
                    registrationResponse,
                });

                if (result.success && result.contentDocumentId) {
                    setUploadStatus((prev) => ({
                        ...prev,
                        [documentType]: {
                            status: 'uploaded',
                            contentDocumentId: result.contentDocumentId,
                        },
                    }));
                    logger.info('useDocumentUpload', 'Document uploaded successfully', {
                        documentType,
                        contentDocumentId: result.contentDocumentId,
                    });
                } else {
                    setUploadStatus((prev) => ({
                        ...prev,
                        [documentType]: {
                            status: 'error',
                            error: result.error || 'Upload failed',
                        },
                    }));
                    logger.error('useDocumentUpload', 'Document upload failed', {
                        documentType,
                        error: result.error,
                    });
                }
            } catch (error: any) {
                setUploadStatus((prev) => ({
                    ...prev,
                    [documentType]: {
                        status: 'error',
                        error: error?.message || 'Upload failed',
                    },
                }));
                logger.error('useDocumentUpload', 'Document upload error', {
                    documentType,
                    error,
                });
            }
        },
        [registrationResponse]
    );

    const resetUploadStatus = useCallback((documentType: DocumentType) => {
        logger.info('useDocumentUpload', 'Resetting upload status', { documentType });
        setUploadStatus((prev) => ({
            ...prev,
            [documentType]: { status: 'idle' },
        }));
    }, []);

    const clearUploadStatus = useCallback((documentType: DocumentType) => {
        logger.info('useDocumentUpload', 'Clearing upload status', { documentType });
        setUploadStatus((prev) => {
            const updated = { ...prev };
            delete updated[documentType];
            // Ensure all document types exist
            return {
                ...INITIAL_UPLOAD_STATUS,
                ...updated,
                [documentType]: { status: 'idle' },
            };
        });
    }, []);

    const retryUpload = useCallback(
        async (documentType: DocumentType, fileUri: string) => {
            logger.info('useDocumentUpload', 'Retry upload called', {
                documentType,
                fileUri,
                currentStatus: uploadStatus[documentType]?.status,
            });

            // Reset status to idle before retrying to allow re-upload
            resetUploadStatus(documentType);

            // Small delay to ensure state update
            await new Promise(resolve => setTimeout(resolve, 100));

            await handleUpload(documentType, fileUri);
        },
        [handleUpload, uploadStatus, resetUploadStatus]
    );

    const isUploading = Object.values(uploadStatus).some(
        (status) => status.status === 'uploading'
    );

    const hasUploading = isUploading;

    return {
        uploadStatus,
        uploadDocument: handleUpload,
        retryUpload,
        resetUploadStatus,
        clearUploadStatus,
        isUploading,
        hasUploading,
        isInitialized,
    };
}

